import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@/App';
import '@/index.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
let ReactQueryDevtools = null;
if (import.meta.env.DEV) {
  // Lazy import in dev to avoid impacting prod bundles
  const mod = await import('@tanstack/react-query-devtools');
  ReactQueryDevtools = mod.ReactQueryDevtools;
}
import { shouldRegisterServiceWorker } from '@/utils/environment';

// Optional query switch: append ?nocache=1 to the URL to force-disable SW and clear caches
const urlParams = new URLSearchParams(window.location.search);
const FORCE_NO_CACHE = urlParams.has('nocache') || urlParams.get('no-sw') === '1';
if (FORCE_NO_CACHE && 'serviceWorker' in navigator) {
  // Best-effort clear of SW and caches before the app mounts
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  }).finally(() => {
    if ('caches' in window) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const container = document.getElementById('root');
if (!container.__RCN_ROOT__) {
  const root = ReactDOM.createRoot(container);
  container.__RCN_ROOT__ = root; // non-standard property to track
  root.render(
    <QueryClientProvider client={queryClient}>
  <App />
  {import.meta.env.DEV && ReactQueryDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
} else {
  // Hot navigation / duplicate script load safety: update existing root
  container.__RCN_ROOT__.render(
    <QueryClientProvider client={queryClient}>
  <App />
  {import.meta.env.DEV && ReactQueryDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}

// Register service worker only in production
if (!FORCE_NO_CACHE && shouldRegisterServiceWorker()) {
  if ('serviceWorker' in navigator) {
    const deferRegister = () => {
      navigator.serviceWorker
  .register('/sw-v8.js', { scope: '/' })
        .then((registration) => {
          console.log('SW registered: ', registration);
          initPush(registration).catch((err) => console.warn('Push init failed', err));
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    };
    // Defer registration until after load and ideally idle to avoid competing with page bootstrap
    window.addEventListener('load', () => {
      if ('requestIdleCallback' in window) {
        // @ts-expect-error - requestIdleCallback not in TS lib by default
        window.requestIdleCallback(deferRegister, { timeout: 2000 });
      } else {
        setTimeout(deferRegister, 0);
      }
    });
  }
} else {
  // When not registering (dev or temporarily disabled in prod), unregister any existing SW
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(function(registrations) {
      for (let registration of registrations) {
        registration.unregister();
      }
    });
  }
}

// Runtime recovery: if a stale chunk causes a React/leaflet createContext error in prod,
// auto-unregister SW and reload once to pull fresh assets.
if (import.meta.env.PROD) {
  const RECOVER_KEY = '__rcn_recover_once__';
  const triggerRecover = () => {
    if (sessionStorage.getItem(RECOVER_KEY)) return;
    sessionStorage.setItem(RECOVER_KEY, '1');
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => {
          regs.forEach((r) => r.unregister());
          window.location.reload();
        })
        .catch(() => window.location.reload());
    } else {
      window.location.reload();
    }
  };

  const pattern = /createContext|leaflet-.*\.js/i;
  window.addEventListener('error', (e) => {
    const msg = (e && (e.message || (e.error && e.error.message))) || '';
    if (pattern.test(String(msg))) triggerRecover();
  });
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e && (e.reason && (e.reason.message || String(e.reason))) || '';
    if (pattern.test(String(msg))) triggerRecover();
  });
}

async function initPush(registration) {
  if (!('PushManager' in window) || !('Notification' in window)) return;
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    console.warn('Missing VAPID public key');
    return;
  }
  // Do not prompt here; browsers require a user gesture for permission prompts.
  // Only proceed if the user has already granted permission.
  if (Notification.permission !== 'granted') return;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return; // already subscribed
  const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: convertedKey
  });
  const { saveSubscription } = await import('@/services/pushSubscriptionService');
  const stored = await saveSubscription(sub);
  console.log('Push subscription created', stored ? 'stored' : 'store-failed');
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function queueNewsletterSubscription(email) {
  if (!('serviceWorker' in navigator)) throw new Error('Service worker not supported');
  const workerUrl = import.meta.env.VITE_DATA_SYNC_WORKER_URL;
  if (!workerUrl) throw new Error('Missing worker configuration');
  const reg = await navigator.serviceWorker.ready;
  reg.active?.postMessage({
    type: 'QUEUE_NEWSLETTER_SUB',
    email,
    config: { workerUrl }
  });
  try {
    if ('sync' in reg) await reg.sync.register('newsletter-sync');
  } catch (e) {
    console.warn('Background sync registration failed', e);
  }
}
